import React, {forwardRef, useMemo} from "react";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import Button from "../../../../../../../components/base/button/button";
import classnames from "classnames";
import {PanelFooter} from "../../../../../../../components/layout";
import {PrintCatalogueViewSteps} from "../../index";

const PrintCatalogueViewBottomActionBar = forwardRef(({
                                                          step,
                                                          productSelectionState,
                                                          configureGroupsState,
                                                          reviewState,
                                                          setStep,
                                                          updatingStep,
                                                      }, ref) => {

    /**@type {boolean}*/
    const show = useMemo(() => {
        let show = false
        switch (step) {
            case PrintCatalogueViewSteps.productSelection:
                if (productSelectionState.selectedCategories.length || productSelectionState.selectedTags.length || productSelectionState.selectedProducts.length)
                    show = true;
                break;
            default :
                show = true;
                break;
        }
        return show;
    }, [productSelectionState.selectedCategories.length, productSelectionState.selectedProducts.length, productSelectionState.selectedTags.length, step])

    /**@type {string}*/
    const title = useMemo(() => {
        switch (step) {
            case PrintCatalogueViewSteps.productSelection:
                return [
                    !productSelectionState.selectedCategories?.length ? '' : `${productSelectionState.selectedCategories?.length ?? 0} Categor${(productSelectionState.selectedCategories?.length ?? 0) === 1 ? 'y' : 'ies'}`,
                    !productSelectionState.selectedProducts?.length ? '' : `${productSelectionState.selectedProducts?.length ?? 0} Product${(productSelectionState.selectedProducts?.length ?? 0) === 1 ? '' : 's'}`,
                    !productSelectionState.selectedTags?.length ? '' : `${productSelectionState.selectedTags?.length ?? 0} Tag${(productSelectionState.selectedTags?.length ?? 0) === 1 ? '' : 's'}`,
                ]
                    .filter(Boolean)
                    .reduce((acc, cur, index, array) => {
                        if (index < array.length - 1)
                            return acc + (acc ? ', ' : '') + cur;
                        return acc + (acc ? ', and ' : '') + cur;
                    }, '').concat(' selected')
            case PrintCatalogueViewSteps.configureGroups:
                return `${configureGroupsState.categories.length} Categor${configureGroupsState.categories.length === 1 ? 'y' : 'ies'}`
                    + ', '
                    + `${configureGroupsState.makes.length} Make${configureGroupsState.makes.length === 1 ? '' : 's'}`
                    + ', and '
                    + `${configureGroupsState.tags.length} Tag${configureGroupsState.tags.length === 1 ? '' : 's'}`
                    + ' selected'
            case PrintCatalogueViewSteps.review:
                return reviewState.products.length + ' Product' + (reviewState.products.length === 1 ? '' : 's') + ' selected'
            default:
                return "";
        }
    }, [configureGroupsState.categories.length, configureGroupsState.makes.length, configureGroupsState.tags.length, productSelectionState.selectedCategories?.length, productSelectionState.selectedProducts?.length, productSelectionState.selectedTags?.length, reviewState.products.length, step])

    /**@type {boolean}*/
    const showPreviousStep = useMemo(() => ![PrintCatalogueViewSteps.productSelection].includes(step), [step])

    /**@type {string}*/
    const previousStepTitle = useMemo(() => {
        if (updatingStep)
            return "Processing..."
        switch (step) {
            case PrintCatalogueViewSteps.configureGroups:
                return "Back to product selection"
            case PrintCatalogueViewSteps.review:
                return "Back to configure groups"
            default:
                return '';

        }
    }, [step, updatingStep])


    /**@type {string}*/
    const nextStepTitle = useMemo(() => {
        if (updatingStep) {
            return "Proceeding..."
        }
        if (step === PrintCatalogueViewSteps.review) {
            return "Print";
        }
        return "Next Step";
    }, [step, updatingStep])

    /**
     * Navigates the user to the previous step of the request.
     * @return {Function | undefined}
     */
    const setPrevStep = () => {
        let newStep;
        switch (step) {
            case PrintCatalogueViewSteps.configureGroups:
                newStep = PrintCatalogueViewSteps.productSelection;
                break;
            case PrintCatalogueViewSteps.review:
                newStep = PrintCatalogueViewSteps.configureGroups;
                break;
            default:
                break;
        }
        if (newStep) {
            setStep(newStep);
        }
    }

    /**
     * Navigates the user to the next step of the request.
     * @return {Function | undefined}
     */
    const setNextStep = () => {
        let newStep;
        switch (step) {
            case PrintCatalogueViewSteps.productSelection:
                newStep = PrintCatalogueViewSteps.configureGroups;
                break;
            case PrintCatalogueViewSteps.configureGroups:
                newStep = PrintCatalogueViewSteps.review;
                break;
            case PrintCatalogueViewSteps.review:
                newStep = 'print';
                break;
            default:
                break;
        }
        if (newStep) {
            setStep(newStep);
        }
    }

    return (
        <>
            {
                show &&
                <PanelFooter>
                    <div className={'bottom-action-bar'}>
                        <div className={classnames('bottom-action-bar-content', {'with-previous-step': showPreviousStep})}>
                            <div className={'d-flex flex-column align-items-start'}>
                                <p>
                                    {title}
                                </p>
                                {
                                    showPreviousStep &&
                                    <div className={'previous-step'}>
                                        <IconButton
                                            disabled={updatingStep}
                                            onPressed={setPrevStep}
                                            color={'paper.secondary'}
                                        >
                                            <ArrowBack className='icon small'/>
                                        </IconButton>
                                        <p>
                                            {previousStepTitle}
                                        </p>
                                    </div>
                                }
                            </div>
                            <Button
                                disabled={updatingStep}
                                onClick={setNextStep}
                                variant={'contained'}
                                color={'primary'}
                            >
                                {nextStepTitle}
                            </Button>
                        </div>
                    </div>
                </PanelFooter>
            }
        </>
    );
})

export default PrintCatalogueViewBottomActionBar;
